import * as React from 'react';
import { onlyDigits } from '@/lib/masks';
import { TextInput } from '@/components/clients/form-primitives';

export interface MaskedInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  /** Valor canonico: apenas digitos (sem mascara). */
  value: string;
  /** Chamado com os digitos limpos apos cada mudanca. */
  onChange: (clean: string) => void;
  /** Funcao que recebe digitos e devolve a string exibida com mascara. */
  mask: (raw: string) => string;
  /** Maximo de digitos aceitos (excedente e descartado). */
  maxDigits: number;
}

/**
 * Input com mascara progressiva que mantem o estado canonico em digitos
 * limpos.
 *
 * - `value` = digitos limpos (ex.: "12345678901")
 * - display = `mask(value)` (ex.: "123.456.789-01")
 * - `onChange` = devolve apenas digitos, truncados em `maxDigits`
 *
 * Resolve o bug de "estado sujo" no paste: ao colar qualquer formato
 * (mascarado, com espacos, com ponto/traco), sempre normaliza para digitos
 * antes de armazenar.
 */
export const MaskedInput = React.forwardRef<HTMLInputElement, MaskedInputProps>(
  ({ value, onChange, mask, maxDigits, ...props }, ref) => {
    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      const clean = onlyDigits(e.target.value).slice(0, maxDigits);
      onChange(clean);
    }

    return (
      <TextInput
        ref={ref}
        {...props}
        value={mask(value)}
        onChange={handleChange}
      />
    );
  },
);

MaskedInput.displayName = 'MaskedInput';
